import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogLead,
  DialogNote,
  type DialogWidth,
  FormAlert,
} from '../Dialog';
import { getErrorMessage } from '../errorMessage';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overline?: ReactNode;
  title: ReactNode;
  lead: ReactNode;
  note?: ReactNode;
  footNote?: ReactNode;
  confirmLabel?: ReactNode;
  confirmVariant?: 'solid' | 'danger';
  /** Действие (мутация); ошибка показывается плашкой в окне. Без действия — только «Закрыть». */
  onConfirm?: () => Promise<unknown>;
  width?: DialogWidth;
  children?: ReactNode;
}

/** Подтверждение действия без полей (окно README «Оверлеи»). */
export function ConfirmDialog({
  open,
  onOpenChange,
  overline,
  title,
  lead,
  note,
  footNote,
  confirmLabel,
  confirmVariant = 'solid',
  onConfirm,
  width = 560,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation('form');
  const [pending, setPending] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);

  function handleOpenChange(next: boolean) {
    if (pending) return;
    if (!next) setAlert(null);
    onOpenChange(next);
  }

  async function confirm() {
    if (!onConfirm) return;
    setPending(true);
    setAlert(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      setAlert(getErrorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent width={width} preventClose={pending}>
        <DialogHeader overline={overline} title={title} hideClose={pending} />
        <DialogBody>
          <DialogLead>{lead}</DialogLead>
          {alert ? <FormAlert>{alert}</FormAlert> : null}
          {children}
          {note ? <DialogNote>{note}</DialogNote> : null}
        </DialogBody>
        <DialogFooter note={footNote}>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={pending}>
            {onConfirm ? t('cancel') : t('close')}
          </Button>
          {onConfirm ? (
            <Button variant={confirmVariant} onClick={confirm} loading={pending}>
              {confirmLabel}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
